/**
 * ResearchInputsOps.jsx — the Inputs and Ops tabs (HANDOFF §5.1).
 *
 * These fold in the old Signals/Library and System/Desk tabs. They read the
 * same summary the Overview does rather than re-fetching the legacy feed
 * endpoints, so feed ages here and on the Overview can never disagree.
 *
 * Panels that are not yet ported say so explicitly and point at the legacy
 * view, rather than rendering an empty skeleton — the old Desk stat panels
 * were blank boxes with no explanation, which is the thing this replaces.
 */

import React from "react";

import { ago, fmtInt, fmtNum } from "../../lib/format";
import { useResearchSummary } from "../../lib/researchApi";

const DASH = "—";

function Card({ title, sub, children }) {
  return (
    <section className="rx-card">
      <header style={{ marginBottom: 12 }}>
        <div className="rx-label">{title}</div>
        {sub && <div className="rx-faint" style={{ fontSize: 11, marginTop: 3 }}>{sub}</div>}
      </header>
      {children}
    </section>
  );
}

function NotPorted({ what, legacyTab }) {
  return (
    <div className="rx-empty">
      {what} has not been moved into the new layout yet. It is unchanged and
      still reachable on the legacy {legacyTab} tab — open this page with
      <code style={{ color: "var(--accent)" }}> ?legacy=1 </code> to use it.
    </div>
  );
}

export function ResearchInputs() {
  const { data, loading, error } = useResearchSummary();
  if (loading) return <div className="rx-empty">Loading inputs…</div>;
  if (error && !data) return <div className="rx-empty">Inputs could not be loaded ({error}).</div>;

  const feeds = data?.feeds || [];

  return (
    <div className="rx-grid" style={{ gap: 14 }}>
      <Card title="Feed health"
            sub="A group is only as fresh as its stalest source, so the oldest member is reported.">
        {feeds.length === 0 ? (
          <div className="rx-empty">No feed sources reported.</div>
        ) : (
          <div className="rx-row">
            {feeds.map((f) => {
              const bad = f.status !== "fresh";
              return (
                <div key={f.name} className="rx-cell"
                     style={{ flex: "1 1 170px",
                              borderColor: bad ? "var(--warn)" : "var(--border)" }}>
                  <div className="rx-label" style={{ fontSize: 11 }}>{f.name}</div>
                  <div className="rx-num" style={{ fontSize: 16, marginTop: 5,
                                                   color: bad ? "var(--warn)" : "var(--text)" }}>
                    {f.fetched_at ? ago(f.fetched_at) : DASH}
                  </div>
                  <div className="rx-faint" style={{ fontSize: 10.5, marginTop: 3 }}>
                    {bad && <span className="rx-tag rx-tag-review" style={{ marginRight: 5 }}>{f.status}</span>}
                    {f.age_s == null ? "never fetched" : `${fmtNum(f.age_s / 60, 0)} minutes old`}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <Card title="Market signals">
        <NotPorted what="The live signals, on-chain and listing-radar panels" legacyTab="Signals" />
      </Card>

      <Card title="Paper library">
        <a className="rx-chip" href="#research/papers">Open Papers →</a>
      </Card>
    </div>
  );
}

export function ResearchOps() {
  const { data, loading, error } = useResearchSummary();
  if (loading) return <div className="rx-empty">Loading ops…</div>;
  if (error && !data) return <div className="rx-empty">Ops could not be loaded ({error}).</div>;

  const r = data?.regime || {};
  const actWhilePaused = r.act_mode && r.engines_paused;

  return (
    <div className="rx-grid" style={{ gap: 14 }}>
      <Card title="NEXUS" sub={r.sentence}>
        <div className="rx-row">
          {[
            ["Regime", r.regime || DASH, r.reason],
            ["Trend", r.trend || DASH, null],
            ["Market entropy", r.entropy_market == null ? DASH : fmtNum(r.entropy_market, 3),
              "normalised Shannon entropy, high = chaotic"],
            ["System health H", r.system_health_h == null ? DASH : fmtNum(r.system_health_h, 3),
              "below 1.0 means not profitable"],
            ["Position sizing", r.position_multiplier == null ? DASH : `×${fmtNum(r.position_multiplier, 2)}`,
              r.engines_paused ? "engine signals paused" : "engines trading"],
            ["Reading age", r.as_of ? ago(r.as_of) : DASH, r.stale ? "STALE" : "fresh"],
          ].map(([label, value, note]) => (
            <div key={label} className="rx-cell" style={{ flex: "1 1 170px" }}>
              <div className="rx-label" style={{ fontSize: 11 }}>{label}</div>
              <div className="rx-num" style={{ fontSize: 16, marginTop: 5 }}>{value}</div>
              {note && <div className="rx-faint" style={{ fontSize: 10.5, marginTop: 3, lineHeight: 1.4 }}>{note}</div>}
            </div>
          ))}
        </div>

        {r.disagreements?.length > 0 && (
          <div className="rx-empty" style={{ marginTop: 12, borderColor: "var(--warn)" }}>
            <strong style={{ color: "var(--warn)" }}>Other sources disagree.</strong>{" "}
            NEXUS is authoritative because it is the reading that actually gates
            engine signals; these describe the market but change nothing:
            <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
              {r.disagreements.map((d, i) => (
                <li key={i} style={{ marginBottom: 3 }}>
                  <span className="rx-num">{d.source}</span> says{" "}
                  <span style={{ color: "var(--text-2)" }}>{d.regime}</span>
                  {d.note ? ` — ${d.note}` : ""}
                </li>
              ))}
            </ul>
          </div>
        )}

        {r.source_errors?.length > 0 && (
          <div className="rx-empty" style={{ marginTop: 12, borderColor: "var(--neg)" }}>
            <strong style={{ color: "var(--neg)" }}>A regime source could not be read,</strong>{" "}
            so agreement between sources is unverified:
            <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
              {r.source_errors.map((e, i) => (
                <li key={i} className="rx-num" style={{ fontSize: 11 }}>{e}</li>
              ))}
            </ul>
          </div>
        )}
      </Card>

      <Card title="ACT mode" sub="The desk's master switch for changing live behaviour.">
        <div className="rx-cell"
             style={{ borderColor: actWhilePaused ? "var(--warn)" : "var(--border)",
                      background: actWhilePaused ? "rgba(242,184,75,.08)" : "var(--panel-2)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span className="rx-num" style={{ fontSize: 20,
                                              color: r.act_mode ? "var(--warn)" : "var(--muted)" }}>
              {r.act_mode == null ? DASH : r.act_mode ? "ON" : "OFF"}
            </span>
            {actWhilePaused && <span className="rx-tag rx-tag-fix">Fix now</span>}
          </div>
          <p className="rx-muted" style={{ fontSize: 12.5, lineHeight: 1.6, margin: "8px 0 0" }}>
            {r.act_mode
              ? "The desk auto-executes leverage, risk and gate changes within its bounds. Engine start/stop still needs manual approval, and a server-side interlock withholds auto-execution whenever no regime source has reported recently."
              : "Proposed actions queue for manual approval instead of executing."}
            {actWhilePaused && " Engines are currently paused, so the desk is tuning engines that are not trading."}
          </p>
          <div className="rx-faint" style={{ fontSize: 11, marginTop: 8 }}>
            This switch is deliberately not operable from this page.
          </div>
        </div>
      </Card>

      <Card title="OMEGA and the Desk">
        <NotPorted what="The OMEGA cycle, self-healing log and desk agent panels" legacyTab="System and Desk" />
      </Card>
    </div>
  );
}
